// src/services/speakerManagementService.ts

import {
  GetTokenWithPopupOptions,
  PopupConfigOptions
} from '@auth0/auth0-react';
import {
  SessionSpeakerMapping,
  SpeakerAssignmentOperationResult,
  AssignPersonToSpeakerRequest,
  UpdateSpeakerAssignmentRequest,
  ValidateSpeakerAssignmentRequest,
  AssociatePersonToSessionRequest,
  UpdateSessionPersonAssignmentRequest
} from '@/types/speakerManagementTypes';
import { Person } from '@/types/peopleTypes';

// Define types for Auth0 functions
type GetAccessTokenSilentlyType = (options?: any) => Promise<string>;
type GetAccessTokenWithPopupType = (
  options?: GetTokenWithPopupOptions,
  config?: PopupConfigOptions
) => Promise<string | undefined>;

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Helper function to acquire Auth0 access token.
 */
const getAuthToken = async (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
    }
  };
  try {
    return await getAccessTokenSilently(authParams);
  } catch (error: any) {
    console.warn(
      'Silent token acquisition failed for SpeakerManagementService:',
      error
    );
    if (
      error.error === 'consent_required' ||
      error.error === 'login_required' ||
      error.error === 'interaction_required'
    ) {
      console.log(
        'Falling back to interactive token acquisition for SpeakerManagementService...'
      );
      const token = await getAccessTokenWithPopup(authParams);
      if (!token) {
        throw new Error('Failed to acquire access token interactively');
      }
      return token;
    }
    throw error;
  }
};

/**
 * Helper function to make authenticated API requests.
 */
const makeAuthenticatedRequest = async (
  url: string,
  options: RequestInit,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<Response> => {
  const token = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `API request failed: ${response.status} ${response.statusText} - ${errorText}`
    );
  }

  return response;
};

/**
 * SpeakerManagementService class for managing speaker assignments and session-person associations.
 */
export class SpeakerManagementService {
  constructor(
    private getAccessTokenSilently: GetAccessTokenSilentlyType,
    private getAccessTokenWithPopup: GetAccessTokenWithPopupType
  ) {}

  // ===== Speaker Management Methods =====

  /**
   * Get all speaker assignments for a session.
   */
  async getSessionSpeakers(sessionId: string): Promise<SessionSpeakerMapping> {
    const response = await makeAuthenticatedRequest(
      `${API_BASE_URL}/sessions/${sessionId}/speakers`,
      { method: 'GET' },
      this.getAccessTokenSilently,
      this.getAccessTokenWithPopup
    );

    return await response.json();
  }

  /**
   * Assign a person to a speaker index.
   */
  async assignPersonToSpeaker(
    sessionId: string,
    request: AssignPersonToSpeakerRequest
  ): Promise<SpeakerAssignmentOperationResult> {
    const response = await makeAuthenticatedRequest(
      `${API_BASE_URL}/sessions/${sessionId}/speakers/assign`,
      {
        method: 'POST',
        body: JSON.stringify(request)
      },
      this.getAccessTokenSilently,
      this.getAccessTokenWithPopup
    );

    return await response.json();
  }

  /**
   * Update a speaker assignment.
   */
  async updateSpeakerAssignment(
    sessionId: string,
    speakerIndex: number,
    request: UpdateSpeakerAssignmentRequest
  ): Promise<SpeakerAssignmentOperationResult> {
    const response = await makeAuthenticatedRequest(
      `${API_BASE_URL}/sessions/${sessionId}/speakers/${speakerIndex}`,
      {
        method: 'PUT',
        body: JSON.stringify(request)
      },
      this.getAccessTokenSilently,
      this.getAccessTokenWithPopup
    );

    return await response.json();
  }

  /**
   * Remove a speaker assignment.
   */
  async removeSpeakerAssignment(
    sessionId: string,
    speakerIndex: number
  ): Promise<boolean> {
    try {
      await makeAuthenticatedRequest(
        `${API_BASE_URL}/sessions/${sessionId}/speakers/${speakerIndex}`,
        { method: 'DELETE' },
        this.getAccessTokenSilently,
        this.getAccessTokenWithPopup
      );
      return true;
    } catch (error: any) {
      if (error.message.includes('404')) {
        return false;
      }
      throw error;
    }
  }

  /**
   * Validate a speaker assignment.
   */
  async validateSpeakerAssignment(
    sessionId: string,
    speakerIndex: number,
    request: ValidateSpeakerAssignmentRequest
  ): Promise<SpeakerAssignmentOperationResult> {
    const response = await makeAuthenticatedRequest(
      `${API_BASE_URL}/sessions/${sessionId}/speakers/${speakerIndex}/validate`,
      {
        method: 'POST',
        body: JSON.stringify(request)
      },
      this.getAccessTokenSilently,
      this.getAccessTokenWithPopup
    );

    return await response.json();
  }

  // ===== Session-Person Association Methods =====

  /**
   * Associate a person to a transcription session.
   */
  async associatePersonToSession(
    sessionId: string,
    request: AssociatePersonToSessionRequest
  ): Promise<boolean> {
    try {
      await makeAuthenticatedRequest(
        `${API_BASE_URL}/transcriptionsessions/${sessionId}/people/associate`,
        {
          method: 'POST',
          body: JSON.stringify(request)
        },
        this.getAccessTokenSilently,
        this.getAccessTokenWithPopup
      );
      return true;
    } catch (error: any) {
      console.error('Failed to associate person to session:', error);
      return false;
    }
  }

  /**
   * Get all people associated with a session.
   */
  async getSessionPeople(sessionId: string): Promise<Person[]> {
    const response = await makeAuthenticatedRequest(
      `${API_BASE_URL}/transcriptionsessions/${sessionId}/people`,
      { method: 'GET' },
      this.getAccessTokenSilently,
      this.getAccessTokenWithPopup
    );

    return await response.json();
  }

  /**
   * Update a person's speaker assignment in a session.
   */
  async updateSessionPersonAssignment(
    sessionId: string,
    personId: string,
    request: UpdateSessionPersonAssignmentRequest
  ): Promise<boolean> {
    try {
      await makeAuthenticatedRequest(
        `${API_BASE_URL}/transcriptionsessions/${sessionId}/people/${personId}/speaker`,
        {
          method: 'PUT',
          body: JSON.stringify(request)
        },
        this.getAccessTokenSilently,
        this.getAccessTokenWithPopup
      );
      return true;
    } catch (error: any) {
      console.error('Failed to update session person assignment:', error);
      return false;
    }
  }

  // ===== Convenience Methods =====

  /**
   * Quick assign person to speaker with minimal parameters.
   */
  async quickAssignPersonToSpeaker(
    sessionId: string,
    speakerIndex: number,
    personId: string,
    speakerLabel?: string
  ): Promise<SpeakerAssignmentOperationResult> {
    return this.assignPersonToSpeaker(sessionId, {
      speakerIndex,
      personId,
      speakerLabel: speakerLabel || `Speaker ${speakerIndex}`,
      isManuallyAssigned: true,
      assignmentMethod: 'manual'
    });
  }

  /**
   * Reassign speaker to different person.
   */
  async reassignSpeaker(
    sessionId: string,
    speakerIndex: number,
    newPersonId: string,
    speakerLabel?: string
  ): Promise<SpeakerAssignmentOperationResult> {
    return this.updateSpeakerAssignment(sessionId, speakerIndex, {
      personId: newPersonId,
      speakerLabel: speakerLabel || `Speaker ${speakerIndex}`
    });
  }

  /**
   * Confirm speaker assignment is correct.
   */
  async confirmSpeakerAssignment(
    sessionId: string,
    speakerIndex: number
  ): Promise<SpeakerAssignmentOperationResult> {
    return this.validateSpeakerAssignment(sessionId, speakerIndex, {
      isValid: true
    });
  }

  /**
   * Reject speaker assignment as incorrect.
   */
  async rejectSpeakerAssignment(
    sessionId: string,
    speakerIndex: number,
    notes?: string
  ): Promise<SpeakerAssignmentOperationResult> {
    return this.validateSpeakerAssignment(sessionId, speakerIndex, {
      isValid: false,
      validationNotes: notes
    });
  }
}

/**
 * Factory function to create SpeakerManagementService instance.
 */
export const createSpeakerManagementService = (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): SpeakerManagementService => {
  return new SpeakerManagementService(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
};
